import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { authService } from '@/services/api';
import { ArrowLeft, Plus, Edit, Trash2, FolderOpen, TrendingUp, DollarSign, Key } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PAYMENT_METHODS = [
  { id: 'banco_plaza_c2p', name: 'Banco Plaza C2P' },
  { id: 'banco_plaza_p2p', name: 'Banco Plaza P2P' },
  { id: 'banco_plaza_transfer', name: 'Banco Plaza Transferencias' },
  { id: 'banco_plaza_debit', name: 'Banco Plaza Débito Inmediato' },
  { id: 'stripe', name: 'Stripe (Tarjetas)' },
  { id: 'paypal', name: 'PayPal' },
  { id: 'crypto_usdt', name: 'USDT (Crypto)' },
  { id: 'crypto_bitcoin', name: 'Bitcoin' }
];

export default function Projects() {
  const navigate = useNavigate();
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [editingProject, setEditingProject] = useState(null);
  const [formData, setFormData] = useState({
    project_name: '',
    project_description: '',
    enabled_payment_methods: []
  });
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadProjects();
  }, []);

  const loadProjects = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${BACKEND_URL}/api/projects`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setProjects(response.data);
    } catch (error) {
      console.error('Error loading projects:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      await axios.post(`${BACKEND_URL}/api/projects`, formData, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Proyecto creado correctamente' });
      setShowCreateModal(false);
      setFormData({
        project_name: '',
        project_description: '',
        enabled_payment_methods: []
      });
      loadProjects();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error al crear proyecto' 
      });
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setMessage({ type: '', text: '' });

    try {
      const token = localStorage.getItem('token');
      await axios.put(
        `${BACKEND_URL}/api/projects/${editingProject.project_id}`,
        formData,
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setMessage({ type: 'success', text: 'Proyecto actualizado correctamente' });
      setEditingProject(null);
      setFormData({
        project_name: '',
        project_description: '',
        enabled_payment_methods: []
      });
      loadProjects();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error al actualizar proyecto' 
      });
    }
  };

  const handleDelete = async (projectId) => {
    if (!window.confirm('¿Estás seguro de archivar este proyecto?')) return;

    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${BACKEND_URL}/api/projects/${projectId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });

      setMessage({ type: 'success', text: 'Proyecto archivado correctamente' });
      loadProjects();
    } catch (error) {
      setMessage({ 
        type: 'error', 
        text: error.response?.data?.detail || 'Error al archivar proyecto' 
      });
    }
  };

  const openEditModal = (project) => {
    setEditingProject(project);
    setFormData({
      project_name: project.project_name,
      project_description: project.project_description || '',
      enabled_payment_methods: project.enabled_payment_methods || []
    });
  };

  const togglePaymentMethod = (methodId) => {
    setFormData(prev => ({
      ...prev,
      enabled_payment_methods: prev.enabled_payment_methods.includes(methodId)
        ? prev.enabled_payment_methods.filter(m => m !== methodId)
        : [...prev.enabled_payment_methods, methodId]
    }));
  };

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#051C2C' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center gap-4">
              <button
                onClick={() => navigate('/dashboard')}
                className="text-white hover:text-gray-300"
              >
                <ArrowLeft className="w-5 h-5" />
              </button>
              <img 
                src="/logos/logo-fondo-oscuro.png" 
                alt="Caura Pay" 
                className="h-8"
              />
              <span className="text-xl font-bold text-white border-l border-white/30 pl-4">
                Proyectos
              </span>
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-white text-gray-800 rounded-lg hover:bg-gray-100 transition-colors"
              data-testid="create-project-button"
            >
              <Plus className="w-4 h-4" />
              Nuevo Proyecto
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {message.text && (
          <div className={`mb-6 p-4 rounded-lg ${
            message.type === 'success' 
              ? 'bg-green-50 border border-green-200 text-green-800'
              : 'bg-red-50 border border-red-200 text-red-800'
          }`}>
            {message.text}
          </div>
        )}

        {/* Info Card */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-6">
          <h3 className="font-semibold text-blue-900 mb-2">Sobre los Proyectos</h3>
          <p className="text-sm text-blue-800">
            Cada proyecto representa una plataforma o servicio independiente con sus propias comisiones,
            API keys y configuraciones de pago. Esto te permite gestionar múltiples productos de forma separada.
          </p>
        </div>

        {/* Projects Grid */}
        {loading ? (
          <div className="text-center py-12 text-gray-500">Cargando proyectos...</div>
        ) : projects.length === 0 ? (
          <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-12 text-center">
            <FolderOpen className="w-16 h-16 mx-auto mb-4 text-gray-300" />
            <h3 className="text-lg font-semibold text-gray-700 mb-2">No tienes proyectos todavía</h3>
            <p className="text-gray-500 mb-6">Crea tu primer proyecto para comenzar</p>
            <button
              onClick={() => setShowCreateModal(true)}
              className="px-6 py-3 text-white rounded-lg font-medium"
              style={{ backgroundColor: '#003594' }}
            >
              Crear Primer Proyecto
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="projects-grid">
            {projects.map((project) => (
              <div
                key={project.project_id}
                className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden hover:shadow-md transition-all"
              >
                <div className="p-6">
                  <div className="flex items-start justify-between mb-4">
                    <div className="flex-1">
                      <h3 className="text-lg font-semibold mb-1" style={{ color: '#051C2C' }}>
                        {project.project_name}
                      </h3>
                      {project.project_description && (
                        <p className="text-sm text-gray-600 line-clamp-2">
                          {project.project_description}
                        </p>
                      )}
                    </div>
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      project.status === 'active' 
                        ? 'bg-green-100 text-green-800'
                        : 'bg-gray-100 text-gray-800'
                    }`}>
                      {project.status}
                    </span>
                  </div>

                  {/* Stats */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <TrendingUp className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-600">Transacciones</span>
                      </div>
                      <p className="text-lg font-bold" style={{ color: '#003594' }}>
                        {project.total_transactions || 0}
                      </p>
                    </div>
                    <div className="bg-gray-50 rounded-lg p-3">
                      <div className="flex items-center gap-2 mb-1">
                        <DollarSign className="w-4 h-4 text-gray-500" />
                        <span className="text-xs text-gray-600">Volumen</span>
                      </div>
                      <p className="text-lg font-bold text-green-600">
                        VES {(project.total_volume || 0).toFixed(2)}
                      </p>
                    </div>
                  </div>

                  {/* Config Info */}
                  <div className="space-y-2 mb-4 pb-4 border-b border-gray-200">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Comisión:</span>
                      <span className="font-semibold" style={{ color: '#003594' }}>
                        {project.fee_percentage}%
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">API Keys:</span>
                      <span className="font-semibold text-yellow-600">
                        {project.api_keys_count}
                      </span>
                    </div>
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-600">Métodos:</span>
                      <span className="font-semibold text-gray-700">
                        {project.enabled_payment_methods?.length || 0}
                      </span>
                    </div>
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2">
                    <button
                      onClick={() => navigate(`/project/${project.project_id}`)}
                      className="flex-1 px-3 py-2 text-sm text-white rounded-lg"
                      style={{ backgroundColor: '#003594' }}
                    >
                      Ver Detalles
                    </button>
                    <button
                      onClick={() => openEditModal(project)}
                      className="px-3 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                      title="Editar"
                    >
                      <Edit className="w-4 h-4 text-gray-600" />
                    </button>
                    {project.status === 'active' && (
                      <button
                        onClick={() => handleDelete(project.project_id)}
                        className="px-3 py-2 border border-red-300 rounded-lg hover:bg-red-50"
                        title="Archivar"
                      >
                        <Trash2 className="w-4 h-4 text-red-600" />
                      </button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {(showCreateModal || editingProject) && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold" style={{ color: '#051C2C' }}>
                {editingProject ? 'Editar Proyecto' : 'Crear Nuevo Proyecto'}
              </h2>
            </div>

            <form onSubmit={editingProject ? handleUpdate : handleCreate} className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Nombre del Proyecto *
                </label>
                <input
                  type="text"
                  value={formData.project_name}
                  onChange={(e) => setFormData({ ...formData, project_name: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Mi Plataforma E-commerce"
                  required
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Descripción
                </label>
                <textarea
                  value={formData.project_description}
                  onChange={(e) => setFormData({ ...formData, project_description: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  placeholder="Descripción del proyecto..."
                  rows={3}
                />
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
                <p className="text-sm text-blue-800">
                  ℹ️ La comisión del proyecto será heredada de tu configuración de comercio (actualmente 2.5%). El super admin puede ajustarla si es necesario.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-3">
                  Métodos de Pago Habilitados
                </label>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
                  {PAYMENT_METHODS.map((method) => (
                    <label
                      key={method.id}
                      className="flex items-center gap-2 p-3 border border-gray-200 rounded-lg hover:bg-gray-50 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={formData.enabled_payment_methods.includes(method.id)}
                        onChange={() => togglePaymentMethod(method.id)}
                        className="w-4 h-4 rounded"
                        style={{ accentColor: '#003594' }}
                      />
                      <span className="text-sm">{method.name}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="flex gap-3 pt-4 border-t">
                <button
                  type="button"
                  onClick={() => {
                    setShowCreateModal(false);
                    setEditingProject(null);
                    setFormData({
                      project_name: '',
                      project_description: '',
                      enabled_payment_methods: []
                    });
                  }}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="flex-1 px-4 py-2 text-white rounded-lg font-medium"
                  style={{ backgroundColor: '#003594' }}
                >
                  {editingProject ? 'Actualizar' : 'Crear'} Proyecto
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
